import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { UserInfo, LoginRequest } from '../types/auth';
import { apiService } from '../services/api';

interface AuthContextType {
  user: UserInfo | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  login: (credentials: LoginRequest) => Promise<void>;
  logout: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth 必須在 AuthProvider 內部使用');
  }
  return context;
};

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<UserInfo | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const initAuth = async () => {
      const token = localStorage.getItem('authToken');
      const savedUser = localStorage.getItem('userInfo');

      if (token && savedUser) {
        try {
          // 驗證 token 是否仍然有效
          const currentUser = await apiService.getCurrentUser();
          setUser(currentUser);
        } catch (error) {
          // Token 無效，清除本地存儲
          localStorage.removeItem('authToken');
          localStorage.removeItem('userInfo');
        }
      }
      setIsLoading(false);
    };

    initAuth();
  }, []);

  const login = async (credentials: LoginRequest) => {
    try {
      // 驗證輸入
      if (!credentials.userId || !credentials.password) {
        throw new Error('用戶名和密碼不能為空');
      }

      const response = await apiService.login(credentials);

      // 驗證響應
      if (!response.token || !response.userId) {
        throw new Error('登入響應無效');
      }

      // 保存 token 和用戶信息
      localStorage.setItem('authToken', response.token);
      localStorage.setItem('userInfo', JSON.stringify({
        userId: response.userId,
        nickName: response.nickName,
        roleId: response.roleId,
        //roleName: response.roleName
      }));

      setUser({
        userId: response.userId,
        nickName: response.nickName,
        roleId: response.roleId,
        //roleName: response.roleName
      });
    } catch (error: any) {
      // 處理不同的錯誤情況
      let errorMessage = '登入失敗';

      if (error.response) {
        // 服務器返回的錯誤
        const responseData = error.response.data;

        // 優先使用標準 ApiResponse 格式
        if (responseData?.message) {
          errorMessage = responseData.message;
        } else if (responseData?.error) {
          errorMessage = responseData.error;
        } else if (typeof responseData === 'string') {
          errorMessage = responseData;
        } else {
          // 根據 HTTP 狀態碼提供默認消息
          switch (error.response.status) {
            case 401:
              errorMessage = '用戶名或密碼錯誤';
              break;
            case 403:
              errorMessage = '帳號已被禁用，請聯繫管理員';
              break;
            case 404:
              errorMessage = '登錄服務不可用';
              break;
            case 500:
              errorMessage = '服務器錯誤，請稍後再試';
              break;
            default:
              errorMessage = '登入失敗，請檢查您的憑據';
          }
        }
      } else if (error.request) {
        // 請求已發送但沒有收到響應（網絡錯誤）
        errorMessage = '無法連接到服務器，請檢查網絡連接';
      } else if (error.message) {
        // 其他錯誤（包括驗證錯誤）
        errorMessage = error.message;
      }

      throw new Error(errorMessage);
    }
  };

  const logout = async () => {
    try {
      await apiService.logout();
    } catch (error) {
      // 即使 logout API 失敗，也要清除本地狀態
      console.error('登出 API 調用失敗:', error);
    } finally {
      // 清除本地存儲和狀態
      localStorage.removeItem('authToken');
      localStorage.removeItem('userInfo');
      setUser(null);
    }
  };

  const value: AuthContextType = {
    user,
    isAuthenticated: !!user,
    isLoading,
    login,
    logout,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};